import React from 'react';
import { Ban, Calendar, ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { CalendarEvent } from '../../types/crm';
import {
  calendarEventIsoDate,
  formatBlockedDayCalendarLabel,
  formatDayDateLabel,
  formatShortUkDate,
  getCalendarSessionLanguage,
  getVenueTypeLabelForCalendarEvent,
  isoDayKey,
  parseIsoDay,
  stripCalendarNoteMeta
} from './trainingCalendarUtils';

/** Colour-coded pill for the COURSE TYPE (a.k.a. delivery mode) column.
 *  Accepts the canonical labels produced by {@link getVenueTypeLabelForCalendarEvent}. */
function CourseTypeBadge({ label }: { label: string }) {
  if (!label || label === '—') {
    return <span className="text-slate-400">—</span>;
  }
  const key = label.toUpperCase();
  const classes: Record<string, string> = {
    'IN CLASS': 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-700/40 dark:text-slate-200 dark:border-slate-600',
    ONLINE: 'bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-900/40 dark:text-sky-200 dark:border-sky-800',
    WEBINAR: 'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-200 dark:border-indigo-800',
    BLENDED: 'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/40 dark:text-teal-200 dark:border-teal-800',
    'ON SITE': 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-800',
  };
  const tone = classes[key] || 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-700/40 dark:text-slate-200 dark:border-slate-600';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wide ${tone}`}>
      {key}
    </span>
  );
}

type DayScheduleRowModel = {
  event: CalendarEvent;
  idx: number;
  isEven: boolean;
  isCancelled: boolean;
  timeDisplay: string;
  language: string;
  courseName: string;
  price: string;
  trainer: string;
  locationLabel: string;
  locationNotes: string;
  interpreterLabel: string;
  bookedCount: number;
  sessionCapacity: number | undefined | null;
  sessionNotes: string;
  ref: string;
  dateDisplay: string;
  spotTone: string;
  rowBgClass: string;
  rowHoverClass: string;
};

function buildDayScheduleRowModel(event: CalendarEvent, idx: number): DayScheduleRowModel {
  const isEven = idx % 2 === 0;
  const isCancelled = (event.status || '').toLowerCase() === 'cancelled';
  const timeDisplay = event.startTime
    ? event.endTime
      ? `${event.startTime} – ${event.endTime}`
      : event.startTime
    : '—';
  const language = getCalendarSessionLanguage(event) || '—';
  const courseName = event.companyName || event.title || '—';
  const price =
    event.resource?.customerPrice != null
      ? `€${Number(event.resource.customerPrice).toFixed(0)}`
      : '—';
  const trainer = event.resource?.instructorName || '—';
  const locationLabel = event.resource?.locationName || '—';
  const locationNotes = event.resource?.locationNotes || '';
  const interpreterLabel = event.resource?.interpreterName || '—';
  const bookedCount = event.resource?.bookedCount ?? event.attendeeCount ?? 0;
  const sessionCapacity = event.resource?.capacity;
  const sessionNotes = stripCalendarNoteMeta(
    event.resource?.sessionNotes || event.resource?.sessionOtherInfo || event.resource?.notes
  );
  const ref = event.resource?.sessionReferenceCode || '—';
  const dateDisplay = formatShortUkDate(parseIsoDay(calendarEventIsoDate(event)));
  const cap = sessionCapacity;
  const spotTone =
    cap != null && cap > 0
      ? cap - bookedCount > 0
        ? 'text-emerald-600 dark:text-emerald-400'
        : 'text-red-600 dark:text-red-500'
      : 'text-slate-600 dark:text-slate-300';

  const rowBgClass = isCancelled
    ? isEven
      ? 'bg-red-50 dark:bg-red-900/30'
      : 'bg-red-100/70 dark:bg-red-900/40'
    : isEven
      ? 'bg-white dark:bg-slate-800'
      : 'bg-slate-50 dark:bg-slate-800/50';
  const rowHoverClass = isCancelled
    ? 'hover:bg-red-100 dark:hover:bg-red-900/50'
    : 'hover:bg-blue-50 dark:hover:bg-slate-700/50';

  return {
    event,
    idx,
    isEven,
    isCancelled,
    timeDisplay,
    language,
    courseName,
    price,
    trainer,
    locationLabel,
    locationNotes,
    interpreterLabel,
    bookedCount,
    sessionCapacity,
    sessionNotes,
    ref,
    dateDisplay,
    spotTone,
    rowBgClass,
    rowHoverClass,
  };
}

interface DayScheduleTableProps {
  /** Single-day mode — omit when using `periodLabel` for multi-day tables. */
  forDate?: Date;
  /** Multi-day mode title, e.g. "August 2026" or "02 Jul – 31 Jul 2026". */
  periodLabel?: string;
  /** Show a DATE column (for week/month/range schedule tables). */
  showDateColumn?: boolean;
  events: CalendarEvent[];
  isLoading?: boolean;
  onEventRowClick: (event: CalendarEvent, e: React.MouseEvent) => void;
  onGoToBookings?: () => void;
  /** When set, shows a date picker in the header */
  onDateChange?: (next: Date) => void;
  blockedDayStatus?: { severity: 'warn' | 'block'; reason?: string } | null;
  onManageBlockedDay?: (date: Date) => void;
}

export function DayScheduleTable({
  forDate,
  periodLabel,
  showDateColumn = false,
  events,
  isLoading,
  onEventRowClick,
  onGoToBookings,
  onDateChange,
  blockedDayStatus,
  onManageBlockedDay,
}: DayScheduleTableProps) {
  const headerDate = forDate ?? null;
  const isToday = headerDate ? headerDate.toDateString() === new Date().toDateString() : false;
  const dayLabel = periodLabel || (headerDate ? formatDayDateLabel(headerDate) : 'Schedule');
  const rowModels = React.useMemo(() => events.map((e, i) => buildDayScheduleRowModel(e, i)), [events]);

  return (
    <div className={`transition-all duration-300 ${isLoading ? 'opacity-50' : 'opacity-100'}`}>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {onDateChange && headerDate && (
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <span className="font-medium">Select date</span>
            <button
              type="button"
              onClick={() => {
                const prev = new Date(headerDate.getFullYear(), headerDate.getMonth(), headerDate.getDate() - 1, 12, 0, 0);
                onDateChange(prev);
              }}
              className="inline-flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
              aria-label="Previous day"
              title="Previous day"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <input
              type="date"
              className="rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm text-slate-900 dark:text-slate-100"
              value={isoDayKey(headerDate)}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                const [y, m, d] = v.split('-').map(Number);
                onDateChange(new Date(y!, m! - 1, d!, 12, 0, 0));
              }}
            />
            <button
              type="button"
              onClick={() => {
                const next = new Date(headerDate.getFullYear(), headerDate.getMonth(), headerDate.getDate() + 1, 12, 0, 0);
                onDateChange(next);
              }}
              className="inline-flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
              aria-label="Next day"
              title="Next day"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </label>
        )}
        <div
          className={`text-base font-bold ${isToday ? 'text-blue-600 dark:text-blue-400' : 'text-slate-800 dark:text-slate-100'}`}
        >
          {dayLabel}
        </div>
        {isToday && (
          <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-full">
            Today
          </span>
        )}
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {events.length} session{events.length !== 1 ? 's' : ''}
        </span>
        {blockedDayStatus && (
          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
            blockedDayStatus.severity === 'block'
              ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
          }`} title={formatBlockedDayCalendarLabel(blockedDayStatus)}>
            {formatBlockedDayCalendarLabel(blockedDayStatus)}
          </span>
        )}
        {onManageBlockedDay && headerDate && (
          <button
            type="button"
            onClick={() => onManageBlockedDay(headerDate)}
            className={`ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold shadow-sm transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-1 ${
              blockedDayStatus
                ? blockedDayStatus.severity === 'block'
                  ? 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-400'
                  : 'bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-300'
                : 'bg-gradient-to-r from-amber-500 to-red-500 text-white hover:from-amber-600 hover:to-red-600 focus:ring-amber-300'
            }`}
            title={
              blockedDayStatus
                ? `This day is ${blockedDayStatus.severity === 'block' ? 'blocked' : 'flagged'}. Click to edit.`
                : 'Block this day or add a warning'
            }
          >
            <Ban className="w-3.5 h-3.5" />
            {blockedDayStatus ? 'Edit blocked day' : 'Manage blocked day'}
          </button>
        )}
      </div>
      {events.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-slate-50 dark:bg-slate-900/30 rounded-xl border border-slate-200 dark:border-slate-700">
          <Calendar className="w-10 h-10 text-slate-300 dark:text-slate-600 mb-3" />
          <div className="text-sm text-slate-500 dark:text-slate-400 font-medium">No training scheduled</div>
          {onGoToBookings && (
            <button
              type="button"
              onClick={onGoToBookings}
              className="mt-3 inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs font-medium rounded-lg hover:shadow-md transition-all"
            >
              <Calendar className="w-3 h-3 mr-1.5" />
              Schedule Training
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="lg:hidden space-y-3 w-full max-w-full min-w-0">
            {rowModels.map((r) => (
              <button
                key={`mobile-${r.idx}-${r.event.id}`}
                type="button"
                onClick={(e) => onEventRowClick(r.event, e)}
                title={r.isCancelled ? 'Cancelled course' : undefined}
                className={`w-full max-w-full min-w-0 text-left rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3 shadow-sm transition-colors ${r.rowBgClass} ${r.rowHoverClass} active:scale-[0.99]`}
              >
                <div className="flex items-start gap-2 min-w-0">
                  <span
                    className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${r.isCancelled ? 'bg-red-500' : 'bg-blue-500'}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div
                      className={`font-semibold text-sm leading-snug ${
                        r.isCancelled
                          ? 'text-red-700 dark:text-red-300 line-through decoration-red-400/70'
                          : 'text-slate-900 dark:text-slate-100'
                      }`}
                    >
                      {r.courseName}
                    </div>
                    {r.isCancelled && (
                      <span className="mt-1 inline-flex items-center rounded border border-red-300 bg-red-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700 dark:border-red-800 dark:bg-red-900/60 dark:text-red-200">
                        Cancelled
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2.5 text-left">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Ref</div>
                    <div className={`font-mono text-xs truncate ${r.isCancelled ? 'text-red-700 dark:text-red-300' : 'text-slate-700 dark:text-slate-200'}`}>{r.ref}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Time</div>
                    <div className="font-mono text-xs text-slate-800 dark:text-slate-100">{r.timeDisplay}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Language</div>
                    <div className="text-xs text-slate-800 dark:text-slate-100">{r.language || '—'}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Price</div>
                    <div className="text-xs font-semibold text-slate-800 dark:text-slate-200">{r.price}</div>
                  </div>
                  <div className="min-w-0 col-span-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Trainer</div>
                    <div className="text-xs text-slate-800 dark:text-slate-100 break-words">{r.trainer}</div>
                  </div>
                  <div className="min-w-0 col-span-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Interpreter</div>
                    <div className="text-xs text-slate-800 dark:text-slate-100 break-words">{r.interpreterLabel}</div>
                  </div>
                  <div className="min-w-0 col-span-2" title={r.locationNotes ? `Location notes: ${r.locationNotes}` : undefined}>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Location</div>
                    <div className="text-xs text-slate-800 dark:text-slate-100 break-words">{r.locationLabel}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Course type</div>
                    <div className="mt-0.5"><CourseTypeBadge label={getVenueTypeLabelForCalendarEvent(r.event)} /></div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Booked</div>
                    <div className={`text-xs font-semibold inline-flex items-center gap-1 ${r.spotTone}`}>
                      <Users className="w-3 h-3 opacity-70 shrink-0" />
                      {r.sessionCapacity != null ? `${r.bookedCount}/${r.sessionCapacity}` : r.bookedCount}
                    </div>
                  </div>
                  
                  
                  {r.sessionNotes ? (
                    <div className="min-w-0 col-span-2">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Notes</div>
                      <div className="text-xs text-slate-600 dark:text-slate-400 line-clamp-4 break-words">{r.sessionNotes}</div>
                    </div>
                  ) : null}
                </div>
              </button>
            ))}
          </div>

          <div className="hidden lg:block overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm max-w-full">
          <table className="w-full text-sm border-collapse min-w-[900px]">
            <thead>
              <tr className="bg-slate-800 dark:bg-slate-900 text-white">
                {showDateColumn ? (
                  <th className="px-2 py-2 text-left text-[10px] font-semibold tracking-wide whitespace-nowrap border-r border-slate-700">
                    DATE
                  </th>
                ) : null}
                <th className="px-2 py-2 text-left text-[10px] font-semibold tracking-wide whitespace-nowrap border-r border-slate-700">
                  REF
                </th>
                <th className="px-2 py-2 text-left text-[10px] font-semibold tracking-wide whitespace-nowrap border-r border-slate-700">
                  COURSE
                </th>
                <th className="px-2 py-2 text-left text-[10px] font-semibold tracking-wide whitespace-nowrap border-r border-slate-700">
                  LANGUAGE
                </th>
                <th className="px-2 py-2 text-left text-[10px] font-semibold tracking-wide whitespace-nowrap border-r border-slate-700">
                  START – FINISH
                </th>
                <th className="px-2 py-2 text-left text-[10px] font-semibold tracking-wide whitespace-nowrap border-r border-slate-700">
                  PRICE
                </th>
                <th className="px-2 py-2 text-left text-[10px] font-semibold tracking-wide whitespace-nowrap border-r border-slate-700">
                  TRAINER
                </th>
                <th className="px-2 py-2 text-left text-[10px] font-semibold tracking-wide whitespace-nowrap border-r border-slate-700">
                  INTERPRETER
                </th>
                <th className="px-2 py-2 text-left text-[10px] font-semibold tracking-wide whitespace-nowrap border-r border-slate-700">
                  LOCATION
                </th>
                <th
                  className="px-2 py-2 text-left text-[10px] font-semibold tracking-wide whitespace-nowrap border-r border-slate-700"
                  title="Delivery mode — In Class / Online / Webinar / Blended / On Site"
                >
                  COURSE TYPE
                </th>
                <th className="px-2 py-2 text-left text-[10px] font-semibold tracking-wide whitespace-nowrap border-r border-slate-700">
                  NO. BOOKED
                </th>
                
                
                <th className="px-2 py-2 text-left text-[10px] font-semibold tracking-wide whitespace-nowrap border-r border-slate-700">
                  SESSION NOTES
                </th>
              </tr>
            </thead>
            <tbody>
              {rowModels.map((r) => (
                  <tr
                    key={r.idx}
                    onClick={(e) => onEventRowClick(r.event, e)}
                    title={r.isCancelled ? 'Cancelled course' : undefined}
                    className={`cursor-pointer border-b border-slate-100 dark:border-slate-700 transition-colors ${r.rowBgClass} ${r.rowHoverClass}`}
                  >
                    {showDateColumn ? (
                      <td className="px-2 py-2 border-r border-slate-100 dark:border-slate-700 whitespace-nowrap text-xs text-slate-700 dark:text-slate-300">
                        {r.dateDisplay}
                      </td>
                    ) : null}
                    <td
                      className={`px-2 py-2 border-r border-slate-100 dark:border-slate-700 font-mono text-[10px] whitespace-nowrap ${
                        r.isCancelled ? 'text-red-700 dark:text-red-300' : 'text-slate-600 dark:text-slate-400'
                      }`}
                    >
                      {r.ref}
                    </td>
                    <td className="px-2 py-2 border-r border-slate-100 dark:border-slate-700 min-w-[140px]">
                      <div className="flex items-start gap-2">
                        <span
                          className={`mt-1 w-2 h-2 rounded-full shrink-0 ${
                            r.isCancelled ? 'bg-red-500' : 'bg-blue-500'
                          }`}
                        />
                        <div className="min-w-0 flex-1">
                          <span
                            className={`font-semibold leading-tight text-xs ${
                              r.isCancelled
                                ? 'text-red-700 dark:text-red-300 line-through decoration-red-400/70'
                                : 'text-slate-900 dark:text-slate-100'
                            }`}
                          >
                            {r.courseName}
                          </span>
                          {r.isCancelled && (
                            <span className="ml-2 inline-flex items-center rounded border border-red-300 bg-red-100 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide text-red-700 dark:border-red-800 dark:bg-red-900/60 dark:text-red-200">
                              Cancelled
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-2 border-r border-slate-100 dark:border-slate-700 whitespace-nowrap text-xs text-slate-700 dark:text-slate-300">
                      {r.language || '—'}
                    </td>
                    <td className="px-2 py-2 border-r border-slate-100 dark:border-slate-700 whitespace-nowrap font-mono text-xs text-slate-700 dark:text-slate-300">
                      {r.timeDisplay}
                    </td>
                    <td className="px-2 py-2 border-r border-slate-100 dark:border-slate-700 whitespace-nowrap font-semibold text-xs text-slate-800 dark:text-slate-200">
                      {r.price}
                    </td>
                    <td className="px-2 py-2 border-r border-slate-100 dark:border-slate-700 whitespace-nowrap text-xs text-slate-700 dark:text-slate-300">
                      {r.trainer}
                    </td>
                    <td className="px-2 py-2 border-r border-slate-100 dark:border-slate-700 whitespace-nowrap text-xs text-slate-700 dark:text-slate-300">
                      {r.interpreterLabel}
                    </td>
                    <td
                      className="px-2 py-2 border-r border-slate-100 dark:border-slate-700 min-w-[100px] text-xs text-slate-700 dark:text-slate-300"
                      title={r.locationNotes ? `Location notes: ${r.locationNotes}` : ''}
                    >
                      {r.locationLabel}
                    </td>
                    <td className="px-2 py-2 border-r border-slate-100 dark:border-slate-700 whitespace-nowrap text-xs">
                      <CourseTypeBadge label={getVenueTypeLabelForCalendarEvent(r.event)} />
                    </td>
                    <td className={`px-2 py-2 border-r border-slate-100 dark:border-slate-700 whitespace-nowrap text-center text-xs font-semibold ${r.spotTone}`}>
                      {r.sessionCapacity != null ? (
                        <span className="inline-flex items-center gap-1 justify-center">
                          <Users className="w-3 h-3 opacity-70" />
                          {r.bookedCount}/{r.sessionCapacity}
                        </span>
                      ) : (
                        r.bookedCount
                      )}
                    </td>
                    
                    
                    <td className="px-2 py-2 text-xs text-slate-600 dark:text-slate-400 max-w-[140px] truncate" title={r.sessionNotes || ''}>
                      {r.sessionNotes || '—'}
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
